import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { PermissionGuard } from '@/components/PermissionGuard'
import { useFilteredAllowedSites } from '@/hooks/useFilteredAllowedSites'
import { FleetOverviewTab } from '@/routes/-components/FleetOverviewTab/FleetOverviewTab'
import { Box, Button } from '@mui/material'
import { createFileRoute, createLink, redirect, retainSearchParams } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import { useCompany } from '@/contexts'
import { CoercedStringArray } from '@/utils/common.schema.ts'
import { AppTabs } from '@/components/Tab/AppTab.tsx'

const SearchSchema = z.object({
  sites: CoercedStringArray.optional()
})

export const Route = createFileRoute('/_authenticated/$companySlug/vehicles/')({
  beforeLoad: ({ context }) => {
    if (!isFeatureEnabled(FEATURES.FLEET)) {
      throw redirect({
        to: '/not-found'
      })
    }
    context.auth.rejectIfMissingPermissions(['VEHICLE_READ'])
  },
  component: FleetId,
  validateSearch: SearchSchema,
  search: {
    middlewares: [retainSearchParams(true)]
  }
})
const LinkButton = createLink(Button)

function FleetId() {
  const { t } = useTranslation('_routes')
  const { sites } = useCompany()
  const desiredSiteIds = Route.useSearch({ select: s => s.sites })
  const { selectedSitesIds } = useFilteredAllowedSites(sites, desiredSiteIds)

  const tabs = useMemo(
    () => [
      {
        label: t('routes.$fleetId_vehicles.main.tabs.overview'),
        value: 'overview' as const,
        content: () => <FleetOverviewTab siteIds={selectedSitesIds} />
      }
    ],
    [t, selectedSitesIds]
  )

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <PageHeader>{t('routes.$fleetId_vehicles.main.mainTitle')}</PageHeader>
        <PermissionGuard permissions={['VEHICLE_WRITE']}>
          <LinkButton
            variant="contained"
            to="/$companySlug/vehicles/create"
            from={Route.fullPath}
          >
            {t('routes.$fleetId_vehicles.main.buttonTitle')}
          </LinkButton>
        </PermissionGuard>
      </Box>

      <AppTabs tabs={tabs} />
    </>
  )
}
